import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { isPlatformAdmin, isProjectAdmin, isProjectLeader, isSuperUser } from '@/util'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { toast } from 'sonner'
import { OrgUserForm } from '../../Forms/OrgUserForm'
import { useCurrentOrganisation } from '../../useCurrentOrganisation'
import { useSessionStorage } from '@/hooks'

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

export function RowActionsMenu({ user }: { user: MessagesSubscriptionsOrganisationUser }) {
  const {
    getSubscriptionsApiListOrganisationUsers,
    deleteSubscriptionsApiDeleteUser,
    postSubscriptionsApiFreezeUser,
    postSubscriptionsApiUnfreezeUser,
    postAuthenticationApiLogoutUser,
  } = useRestClient()
  const { id: orgId } = useCurrentOrganisation()
  const [userRole] = useSessionStorage('roles', 1)

  const invalidateOrgUsers = () =>
    getSubscriptionsApiListOrganisationUsers.invalidateQueries({
      parameters: {
        query: {
          orgId,
        },
      },
    })

  const isDeletePermitted =
    ((isProjectAdmin(userRole) || isProjectLeader(userRole)) && userRole >= user.roles) ||
    isPlatformAdmin(userRole) ||
    isSuperUser(userRole)

  const isEndSessionPermitted = isProjectLeader(userRole) || isPlatformAdmin(userRole) || isSuperUser(userRole)

  const isFreezePermitted =
    ((isProjectAdmin(userRole) || isProjectLeader(userRole)) && userRole >= user.roles) ||
    isPlatformAdmin(userRole) ||
    isSuperUser(userRole)

  const isUnfreezePermitted = isPlatformAdmin(userRole) || isSuperUser(userRole)

  const { mutate: deleteUser } = deleteSubscriptionsApiDeleteUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User disabled successfully.')
        invalidateOrgUsers()
      },
      onError: () => {
        toast.error('Failed to disable user.')
      },
    }
  )

  const { mutate: freezeUser } = postSubscriptionsApiFreezeUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User frozen successfully.')
        invalidateOrgUsers()
      },
      onError: () => {
        toast.error('Failed to freeze user.')
      },
    }
  )

  const { mutate: unfreezeUser } = postSubscriptionsApiUnfreezeUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User unfrozen successfully.')
        invalidateOrgUsers()
      },
      onError: () => {
        toast.error('Failed to ufreeze user.')
      },
    }
  )

  const { mutate: endUserSession } = postAuthenticationApiLogoutUser.useMutation(
    { path: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User session ended successfully.')
        invalidateOrgUsers()
      },
      onError: () => {
        toast.error('Failed to end user session.')
      },
    }
  )

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          // This is necessary to maintain the open state when we want to keep it open
          if (!open) {
            // Check if we need to prevent closing
            // This is a placeholder for custom logic you might need
            // We'll use other mechanisms to prevent closing on item click
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="size-8">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => {
            // Prevent focus returning to trigger which can close the menu
            e.preventDefault()
          }}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <OrgUserForm
            selectedUser={user}
            sheetTitle="Edit user"
            sheetDescription="Edit the details of the user."
            sheetTrigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Edit user</DropdownMenuItem>}
            onSubmit={() => invalidateOrgUsers()}
          />
          <ConfirmDialog
            trigger={
              <DropdownMenuItem disabled={!isDeletePermitted} onSelect={(e) => e.preventDefault()}>
                Delete user
              </DropdownMenuItem>
            }
            title="Delete user"
            description="Please confirm that the user should be removed from the organisation?"
            onConfirm={() => deleteUser()}
          />
          <ConfirmDialog
            trigger={
              <DropdownMenuItem disabled={!isFreezePermitted} onSelect={(e) => e.preventDefault()}>
                Freeze user
              </DropdownMenuItem>
            }
            title="Freeze user"
            description="Please confirm that the user should be frozen for the organisation?"
            onConfirm={() => freezeUser()}
          />
          <ConfirmDialog
            trigger={
              <DropdownMenuItem disabled={!isUnfreezePermitted} onSelect={(e) => e.preventDefault()}>
                Unfreeze user
              </DropdownMenuItem>
            }
            title="Unfreeze user"
            description="Please confirm that the user should be unfrozen for the organisation?"
            onConfirm={() => unfreezeUser()}
          />
          <ConfirmDialog
            trigger={
              <DropdownMenuItem disabled={!isEndSessionPermitted} onSelect={(e) => e.preventDefault()}>
                End user session
              </DropdownMenuItem>
            }
            title="End user session"
            description="Please confirm that the user's session should be ended?"
            onConfirm={() => endUserSession()}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
